import { isMetaPixelEnabled } from "./enabled"

// Typed facade over the Meta Pixel. Nothing outside this module touches
// `window.fbq`: call sites get a checked signature, and the "pixel is not
// there" case — ad blocker, gate off, loader still in flight — is the common
// path, not an exception.

type FbqOptions = { eventID: string }

type Fbq = {
  (command: "init", pixelId: string): void
  (
    command: "track",
    event: MetaStandardEvent,
    params?: Record<string, unknown>,
    options?: FbqOptions
  ): void
  queue?: unknown[]
  loaded?: boolean
}

declare global {
  interface Window {
    fbq?: Fbq
  }
}

/**
 * Meta's standard events. Custom events are deliberately absent: only standard
 * events can be used as an optimization goal or as a lookalike source, which is
 * the entire reason this pixel exists.
 */
export type MetaStandardEvent =
  | "PageView"
  | "ViewContent"
  | "InitiateCheckout"
  | "Purchase"
  | "CompleteRegistration"

export type MetaContent = {
  /** Offer slug — the one key shared by landing, checkout and purchase. */
  contentId: string
  contentName: string
  /** Amount the buyer would pay, in currency units (reais, not cents). */
  value?: number
  /** ISO 4217. Normalized to upper case here; the DB holds both "BRL" and "brl". */
  currency?: string
}

export type MetaPurchase = MetaContent & {
  value: number
  currency: string
  /**
   * Dedupe key shared with the Conversions API. When the server later sends the
   * same Purchase from the Stripe/Pagar.me webhook, both sides must carry this
   * id or Meta counts one sale twice. Nothing sends it yet — this is the seam
   * the CAPI work plugs into.
   */
  eventId?: string
}

type QueuedCall = [
  MetaStandardEvent,
  Record<string, unknown> | undefined,
  FbqOptions | undefined,
]

/**
 * `/scripts/pixel.js` loads with `afterInteractive`, so `window.fbq` does not
 * exist during the hydration commit — and that is exactly when a checkout page
 * mounts and fires `InitiateCheckout`. Without a buffer those first events,
 * the most valuable ones, would be dropped in silence.
 *
 * Bounded on purpose: when an ad blocker kills the loader outright, nothing
 * ever flushes and the buffer must not grow with every navigation.
 */
const pending: QueuedCall[] = []
const MAX_PENDING = 20

function send([event, params, options]: QueuedCall, fbq: Fbq) {
  if (options) fbq("track", event, params, options)
  else if (params) fbq("track", event, params)
  else fbq("track", event)
}

/** Called from the loader's `onLoad`; also runs on every track attempt. */
export function flushMetaPixelQueue() {
  if (typeof window === "undefined") return
  const fbq = window.fbq
  if (typeof fbq !== "function" || pending.length === 0) return
  const calls = pending.splice(0, pending.length)
  for (const call of calls) {
    try {
      send(call, fbq)
    } catch (err) {
      // Telemetry must never break a screen — same contract as `analytics`.
      console.error(`[meta-pixel] track ${call[0]} failed:`, err)
    }
  }
}

function track(
  event: MetaStandardEvent,
  params?: Record<string, unknown>,
  options?: FbqOptions
) {
  if (!isMetaPixelEnabled()) return
  if (typeof window === "undefined") return
  if (pending.length < MAX_PENDING) pending.push([event, params, options])
  flushMetaPixelQueue()
}

function contentParams(content: MetaContent): Record<string, unknown> {
  const params: Record<string, unknown> = {
    content_ids: [content.contentId],
    content_name: content.contentName,
    content_type: "product",
  }
  if (typeof content.value === "number") params.value = content.value
  if (content.currency) params.currency = content.currency.toUpperCase()
  return params
}

export const metaPixel = {
  /**
   * Every PageView, the first one included — `pixel.js` only runs `init`.
   * See `MetaPixelPageView`.
   */
  pageView() {
    track("PageView")
  },

  /** Sales landing for a course or a product — top of the funnel. */
  viewContent(content: MetaContent) {
    track("ViewContent", contentParams(content))
  },

  /** Buyer reached the checkout screen. */
  initiateCheckout(content: MetaContent) {
    track("InitiateCheckout", contentParams(content))
  },

  /** Payment approved in the browser. Pix/boleto confirm by webhook and never reach this. */
  purchase(purchase: MetaPurchase) {
    track(
      "Purchase",
      contentParams(purchase),
      purchase.eventId ? { eventID: purchase.eventId } : undefined
    )
  },

  /** Profile completed after sign-up — a free, non-monetary conversion. */
  completeRegistration(params?: { contentName?: string }) {
    track(
      "CompleteRegistration",
      params?.contentName ? { content_name: params.contentName } : undefined
    )
  },
}
